import { useEffect } from "react";
import { getSessionConfig } from "@webuild-ai/react-auth/config";

/**
 * Development-only diagnostic for session timeout behaviour.
 *
 * Session expiry depends on three things agreeing, and when it silently fails to fire
 * there is no way to tell which one is wrong from the outside. This logs all three on an
 * interval so the failing one is obvious.
 *
 * 1. The resolved config, which tells you whether `VITE_SESSION_TIMEOUT_MINUTES` was picked up.
 * 2. The auth gate, since `SessionTimeoutProvider` computes
 *    `enabled && !isLoading && isAuthenticated` and does nothing at all until that is true.
 * 3. The persisted session timestamps, since the check interval returns early when either
 *    `webuild_session_start` or `webuild_last_activity` is missing.
 *
 * Runs only when `VITE_SESSION_DEBUG=true` so it costs nothing otherwise, and never in a
 * production build.
 */
export function useSessionDebug(args: {
  isLoading: boolean;
  isAuthenticated: boolean;
  tenant: string;
}): void {
  const { isLoading, isAuthenticated, tenant } = args;

  useEffect(() => {
    if (!import.meta.env.DEV || import.meta.env.VITE_SESSION_DEBUG !== "true") {
      return;
    }

    const report = () => {
      const config = getSessionConfig(tenant);
      const start = Number(localStorage.getItem("webuild_session_start"));
      const last = Number(localStorage.getItem("webuild_last_activity"));
      const now = Date.now();

      const seconds = (ms: number) => `${Math.round(ms / 1000)}s`;

      // Logged as one flat string rather than an object, because consoles and log-capture
      // tools truncate object previews after a handful of keys and the fields that matter
      // most for diagnosis are the last ones.
      const sessionEnabled = !isLoading && isAuthenticated;

      // eslint-disable-next-line no-console
      console.log(
        // `||` rather than `??` so a declared-but-empty VITE_SESSION_TIMEOUT_MINUTES= reads
        // as "unset" instead of printing nothing after the equals sign.
        `[session-debug] env=${import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || "unset"} ` +
          `tenant=${tenant} absolute=${seconds(config.absoluteTimeoutMs)} ` +
          `idle=${seconds(config.idleTimeoutMs)} warn=${seconds(config.warningBeforeExpiryMs)} ` +
          `check=${seconds(config.checkIntervalMs)} | ` +
          // If sessionEnabled is false the provider is inert, whatever the config says.
          `enabled=${sessionEnabled} loading=${isLoading} authed=${isAuthenticated} | ` +
          // If either timestamp is missing the check interval returns before evaluating expiry.
          `start=${start ? seconds(now - start) + " ago" : "MISSING"} ` +
          `lastActivity=${last ? seconds(now - last) + " ago" : "MISSING"} ` +
          `expiresIn=${start ? seconds(start + config.absoluteTimeoutMs - now) : "n/a"}`,
      );
    };

    report();
    const timer = window.setInterval(report, 5_000);

    return () => window.clearInterval(timer);
  }, [isLoading, isAuthenticated, tenant]);
}

export default useSessionDebug;
